import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Info, RefreshCw, RotateCcw } from "lucide-react";
import AppContext from "../../context/AppContext";
import translations from "../common/Translations";
import { isRTL } from "../../utils/rtl";
import { toast } from "../../utils/toast";
import { isSuperAdmin } from "../../utils/permissions";
import ModalCloseButton from "../common/ModalCloseButton";
import ReportFailedStatus from "../common/ReportFailedStatus";
import ReportNoRecordsStatus from "../common/ReportNoRecordsStatus";
import CommonTooltip from "../common/CommonTooltip";
import PagerArrowButton from "../common/PagerArrowButton";
import {
  exportInventoryItems,
  fetchInventoryExportHistory,
} from "../../service/MeterInventoryManagement";
import { InlineSpinner, apiErrorMessage, firstNumber, useInventoryScope } from "./inventoryFormShared";

const POLL_MS = 4000;
const PAGE_SIZE = 10;
// Long enough that a double-click can't queue a second export.
const SUBMIT_COOLDOWN_MS = 1500;
// Bubble sizing shared by every CommonTooltip in this modal.
const COMMON_TOOLTIP_CLASS = "w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]";

const statusCode = (status) => String(status || "").toUpperCase();

const fillTemplate = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, Number(value ?? 0).toLocaleString()),
    template
  );

// projectIds comes back as a comma-separated string ("1" or "1,2").
const parseProjectIds = (value) =>
  (Array.isArray(value) ? value : String(value ?? "").split(","))
    .map((id) => Number(String(id).trim()))
    .filter((id) => Number.isSafeInteger(id) && id > 0);

// Timestamps already arrive in Oman time, so the wall-clock parts are shown as
// they are instead of going through the browser's timezone.
const formatOmanTime = (value, langMode, fallback) => {
  if (!value) return fallback;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  if (!match) return String(value);
  const [, year, month, day, hour, minute] = match.map(Number);
  return new Date(Date.UTC(year, month - 1, day, hour, minute)).toLocaleString(
    langMode === "ar" ? "ar" : langMode === "ur" ? "ur" : "en-GB",
    { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }
  );
};

/**
 * Queues an inventory export and reports the outcome as a toast. Only header
 * code 600 is a success; anything else (601 validation, 608 access denied)
 * arrives as HTTP 200 and is surfaced with the backend's message.
 */
const requestInventoryExport = async (payload, t) => {
  try {
    const data = await exportInventoryItems(payload);
    toast.success(data?.body?.value?.message || t.inventoryExportStarted);
    return data?.body?.value ?? {};
  } catch (error) {
    toast.error(apiErrorMessage(error, t.inventoryExportStartFailed));
    return null;
  }
};

/**
 * Export dialog for the inventory list, laid out like the task list's export
 * modal: the recent exports with their live progress, and Export in the footer.
 * `getExportPayload` is read at click time so the export always matches what the
 * grid is showing. While any export is Pending the list re-reads itself; the bar
 * holds at 99% during the file upload and only reaches 100 once the status turns
 * Completed.
 */
export const InventoryExportModal = ({ open, onClose, getExportPayload }) => {
  const cancelButtonRef = useRef(null);
  const { theme, langMode } = useContext(AppContext);
  const isRtl = isRTL(langMode);
  const t = translations[langMode] || translations.en;
  const { organizationId } = useInventoryScope();
  const scopedOrganizationId = isSuperAdmin() ? organizationId : undefined;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  useEffect(() => {
    if (open) setPage(0);
  }, [open]);

  const loadHistory = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setLoading(true);
      try {
        const value = await fetchInventoryExportHistory({
          page,
          size: PAGE_SIZE,
          organizationId: scopedOrganizationId,
        });
        const list = Array.isArray(value?.exportList) ? value.exportList : [];
        setRows(list);
        setTotal(firstNumber(value?.count, list.length) ?? 0);
      } catch (error) {
        // A failed background refresh keeps the rows already on screen.
        if (!silent) {
          setRows([]);
          setTotal(0);
          toast.error(apiErrorMessage(error, t.inventoryExportHistoryLoadFailed));
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [page, scopedOrganizationId, t]
  );

  useEffect(() => {
    if (open) loadHistory();
  }, [open, loadHistory]);

  const hasPending = rows.some((row) => statusCode(row.status) === "PENDING");
  useEffect(() => {
    if (!open || !hasPending) return undefined;
    const timer = setTimeout(() => loadHistory({ silent: true }), POLL_MS);
    return () => clearTimeout(timer);
  }, [open, hasPending, rows, loadHistory]);

  // A new export lands at the top of page 0, so the list jumps back there.
  const afterQueued = () => {
    if (page === 0) loadHistory();
    else setPage(0);
  };

  const handleExport = async () => {
    if (submitting) return;
    setSubmitting(true);
    const result = await requestInventoryExport(getExportPayload(), t);
    if (result) afterQueued(result);
    setTimeout(() => setSubmitting(false), SUBMIT_COOLDOWN_MS);
  };

  // A retry replays the failed export's own filters, not the screen's current ones.
  const handleRetry = async (item) => {
    setRetryingId(item.id);
    const projectIds = parseProjectIds(item.projectIds);
    const result = await requestInventoryExport(
      {
        ...(projectIds.length ? { projectIds } : {}),
        ...(Array.isArray(item.columnFilters) && item.columnFilters.length
          ? { columnFilters: item.columnFilters }
          : {}),
        ...(scopedOrganizationId ? { organizationId: Number(scopedOrganizationId) } : {}),
      },
      t
    );
    setRetryingId(null);
    if (result) afterQueued(result);
  };

  // Rows are titled by project; the ids are only a fallback for rows that
  // predate projectNames.
  const projectTitle = (item) => {
    const names = (Array.isArray(item.projectNames) ? item.projectNames : []).filter(Boolean);
    if (names.length) return names.join(", ");
    const projectIds = parseProjectIds(item.projectIds);
    return projectIds.length ? projectIds.join(", ") : t.notAvailable;
  };

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" initialFocus={cancelButtonRef} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full justify-center p-2 sm:p-4 text-center items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                dir={isRtl ? "rtl" : "ltr"}
                className={`relative flex flex-col max-h-[85vh] sm:max-h-[90vh] w-full max-w-lg rounded-xl bg-white shadow-2xl transition-all ${
                  theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""
                }`}
                data-testid="inventory-export-modal"
              >
                {/* Header */}
                <div className="flex shrink-0 items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 border-b dark:border-gray-700">
                  <div>
                    <h3 className="text-base text-start font-semibold text-gray-900 dark:text-white">{t.inventoryExportTitle}</h3>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{t.inventoryExportDescription}</p>
                  </div>
                  <ModalCloseButton onClick={onClose} />
                </div>

                {/* Body */}
                <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 sm:py-5 space-y-4 sm:space-y-5 text-start">
                  {/* Recent exports */}
                  <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{t.inventoryExportRecent}</p>
                      <CommonTooltip
                        text={t.refresh}
                        position="top"
                        disableDefaultDescription
                        tooltipClassName={COMMON_TOOLTIP_CLASS}
                        fitContent
                        clampToViewport
                      >
                        <button
                          type="button"
                          onClick={() => loadHistory()}
                          disabled={loading}
                          aria-label={t.refresh}
                          className="text-gray-400 hover:text-gray-600 disabled:opacity-50 dark:hover:text-gray-200"
                          data-testid="inventory-export-history-refresh"
                        >
                          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                        </button>
                      </CommonTooltip>
                    </div>

                    <div className="mb-3 flex items-start gap-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/50 dark:bg-amber-950/30">
                      <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-amber-400" />
                      <p className="text-xs leading-5 text-amber-800 dark:text-amber-300">
                        <span className="font-semibold">{t.noteLabel}</span> {t.inventoryExportNote}
                      </p>
                    </div>

                    {/* Rows follow the task export modal's Recent Reports list: what was
                        exported and when on the left, one status control on the right. */}
                    <div className="max-h-48 space-y-2 overflow-y-auto" data-testid="inventory-export-history">
                      {loading && rows.length === 0 ? (
                        <div className="flex justify-center py-4">
                          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                        </div>
                      ) : rows.length === 0 ? (
                        <p className="py-2 text-sm text-gray-500 dark:text-gray-400">{t.inventoryExportHistoryEmpty}</p>
                      ) : (
                        rows.map((item) => {
                          const code = statusCode(item.status);
                          const percent = Math.min(Math.max(Number(item.progressPercentage) || 0, 0), 100);
                          const showProgress = code === "PENDING" && item.totalCount != null;
                          return (
                            <div
                              key={item.id}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-gray-100 p-2.5 sm:px-3 sm:py-2 dark:border-gray-700"
                              data-testid="inventory-export-history-row"
                            >
                              <div className="min-w-0 flex-1">
                                {/* One line only: long project names are cut off with an
                                    ellipsis, and the full name shows in the common tooltip on hover. */}
                                <CommonTooltip
                                  text={projectTitle(item)}
                                  position="top"
                                  disableDefaultDescription
                                  className="max-w-full cursor-default"
                                  tooltipClassName="w-max max-w-[min(30rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                                  fitContent
                                  clampToViewport
                                >
                                  <p className="min-w-0 truncate text-sm font-medium text-gray-800 dark:text-gray-200">
                                    {projectTitle(item)}
                                  </p>
                                </CommonTooltip>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatOmanTime(item.requestedAt, langMode, "-")}
                                </p>
                                {showProgress && (
                                  <div className="mt-1.5">
                                    <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                                      <div
                                        className="h-full rounded-full bg-[#236DB4] transition-[width] duration-500"
                                        style={{ width: `${percent}%` }}
                                      />
                                    </div>
                                    <p className="mt-1 text-[11px] text-gray-500 tabular-nums dark:text-gray-400">
                                      {fillTemplate(t.inventoryExportProgress, {
                                        completed: item.completedCount,
                                        total: item.totalCount,
                                        remaining: item.remainingCount,
                                      })}
                                    </p>
                                  </div>
                                )}
                              </div>

                              {code === "FAILED" ? (
                                <div className="flex self-start sm:self-auto shrink-0 items-center gap-1.5">
                                  <ReportFailedStatus errorMessage={item.errorMessage} variant="chip" useCommonTooltip />
                                  {/* Same tooltip component as the Failed chip beside it. */}
                                  <CommonTooltip
                                    text={retryingId === item.id ? t.inventoryExportRetrying : t.inventoryExportRetryTooltip}
                                    position="top"
                                    disableDefaultDescription
                                    tooltipClassName={COMMON_TOOLTIP_CLASS}
                                    fitContent
                                    clampToViewport
                                  >
                                    <button
                                      type="button"
                                      onClick={() => handleRetry(item)}
                                      disabled={retryingId != null}
                                      aria-label={t.inventoryExportRetryTooltip}
                                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200"
                                      data-testid="inventory-export-retry"
                                    >
                                      {retryingId === item.id ? <InlineSpinner size={13} /> : <RotateCcw size={13} />}
                                      {t.inventoryExportRetry}
                                    </button>
                                  </CommonTooltip>
                                </div>
                              ) : item.isNoRecordsFound ? (
                                // The filters matched nothing: finished, but there is no file worth downloading.
                                <ReportNoRecordsStatus
                                  message={item.errorMessage?.trim() || t.inventoryExportNoRecordsTooltip}
                                  variant="chip"
                                  className="self-start sm:self-auto shrink-0"
                                  useCommonTooltip
                                />
                              ) : code === "COMPLETED" && item.fileUrl ? (
                                <a
                                  href={item.fileUrl}
                                  download={item.fileName || true}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  // title={item.fileName || t.download}
                                  className="inline-flex self-start sm:self-auto shrink-0 items-center rounded-lg bg-[#236DB4] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#1d5a96]"
                                  data-testid="inventory-export-download"
                                >
                                  {t.download}
                                </a>
                              ) : (
                                <span className="inline-flex self-start sm:self-auto shrink-0 items-center rounded-lg bg-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 tabular-nums dark:bg-gray-600 dark:text-gray-300">
                                  {showProgress ? `${percent}%` : t.inventoryExportPreparing}
                                </span>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>

                    {total > PAGE_SIZE && (
                      <div className="mt-3 flex items-center justify-end gap-3 text-xs text-gray-500">
                        <PagerArrowButton
                          direction="prev"
                          isRtl={isRtl}
                          onClick={() => setPage((prev) => Math.max(prev - 1, 0))}
                          disabled={page === 0}
                        />
                        <span className="min-w-[38px] text-center tabular-nums text-gray-600">
                          {page + 1} / {totalPages}
                        </span>
                        <PagerArrowButton
                          direction="next"
                          isRtl={isRtl}
                          onClick={() => setPage((prev) => Math.min(prev + 1, totalPages - 1))}
                          disabled={page >= totalPages - 1}
                        />
                      </div>
                    )}
                  </div>

                </div>

                {/* Footer */}
                <div className="flex shrink-0 justify-end items-center gap-2.5 px-4 sm:px-6 py-3 border-t dark:border-gray-700 bg-white dark:bg-slate-900 rounded-b-xl">
                  <button
                    data-button-intent="danger"
                    ref={cancelButtonRef}
                    type="button"
                    onClick={onClose}
                    className="inline-flex h-8 items-center justify-center rounded-lg bg-[#FF0000] px-3.5 text-sm font-medium text-white transition-colors hover:bg-[#e60000] focus:outline-none focus:ring-2 focus:ring-red-300"
                  >
                    {t.cancle}
                  </button>
                  <button
                    data-button-intent="primary"
                    type="button"
                    onClick={handleExport}
                    disabled={submitting}
                    className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg bg-[#236DB4] px-3.5 text-sm font-medium text-white transition-colors hover:bg-[#1d5a96] focus:outline-none focus:ring-2 focus:ring-blue-300 disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid="inventory-export-submit"
                  >
                    {submitting && <InlineSpinner size={14} />}
                    {t.inventoryExport}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default InventoryExportModal;
